<template>
  <async-block :loading="loading">
    <div class="transcription" :class="{ processing: !hasStatus('done') }">
      <div class="request-transcription full-width" v-show="!hasTranscription">
        <h2>Request video transcription</h2>
        <p>
          Automatically transcribe speech from your video for closed captioning, interactive time stamps, and video search! Videos are generally transcribed in less than 10 minutes.
        </p>
        <a href="#" class="cta" @click.prevent="requestTranscription">
          <img src="@img/icons-transcribe.svg" />
          Request transcription
        </a>
      </div>
      <div v-show="hasTranscription">
        <div class="processing-transcription full-width" v-show="hasStatus('working') || hasStatus('pending')">
          <img src="@img/illustrations-transcription-processing.svg"/>
          <h2>Transcription processing...</h2>
          <p>You will receive an email when transcription is available</p>
        </div>
        <div class="transcription-sentences" v-show="hasStatus('done')">
          <div class="copy-transcription" ref="copyTranscription" :data-clipboard-text="transcription.attributes.transcript" data-testid="copy-transcript-to-clipboard">
            Copy
            <span v-html="require(`!html-loader!images/icons-clipboard.svg`)" />
          </div>
          <div class="sentence" v-for="(sentence, index) in transcription.attributes.data" :key="index">
            <div class="sentence-time">
              <span class="time-badge" @click="setVideoTime(sentence.start)">
                {{secsToHuman(sentence.start)}}
                <i class="fas fa-arrow-circle-right arrow-right"></i>
              </span>
            </div>
            <div class="sentence-content">{{sentence.words}}</div>
          </div>
        </div>
        <div class="retry-transcription full-width" v-show="hasStatus('failed')">
          <p>
           There was a problem generating your transcription. Please try again.
          </p>
          <a href="#" class="cta" @click.prevent="retryTranscription">
            <img src="@img/icons-transcribe.svg" />
            Retry transcription
          </a>
        </div>
      </div>
    </div>
  </async-block>
</template>

<script>
import { mapState } from 'vuex';
import EventBus from '@/src/event_bus';
import AsyncBlock from '@cmp/base/async_block.vue';
import track from '@/src/tracking';

export default {
  components: { AsyncBlock },
  created: function(){
    const vm = this;
    const transcriptionId = this.item.relationships.transcription?.data?.id;

    const pusher = vm.$root.$pusher.subscribe(`item_${this.item.id}_v5`)
    pusher.bind('transcription_done', (data) => vm.$store.commit('setTranscription', data.data.transcription))

    if(!transcriptionId) {
      vm.loading = false;
      return
    }

    vm.$store.dispatch('getTranscription', transcriptionId).then(() => {
      vm.loading = false;
      vm.$nextTick(() => {
          const clipboard = new ClipboardJS(vm.$refs.copyTranscription);
          clipboard.on('success', () => vm.$root.toastSuccess('Transcription copied to clipboard.'));
          clipboard.on('error', () => vm.$root.toastError('Could not copy transcription, try again later.'));
      })
    });
  },
  data: function(){
    return {
      loading: true
    }
  },
  computed: {
    ...mapState(['item', 'transcription']),
    done: function(){
      return this.transcription.id && this.transcription.attributes.status == 'done'
    },
    hasTranscription: function(){
      return this.transcription.id;
    }
  },
  methods: {
    hasStatus: function(status) {
      return this.transcription.id && this.transcription.attributes.status == status;
    },
    requestTranscription: function(){
      track('transcription_requested')
      this.$store.dispatch('createTranscription', this.item.id);
    },
    retryTranscription: function(){
      this.$store.dispatch('retryTranscription', this.transcription.id);
    },
    setVideoTime: function(time){
      EventBus.$emit('setVideoTime', time);
    },
    secsToHuman: function(secs){
      let t = secs % 3600
      const m = parseInt(t / 60)
      t = t % 60
      const s = parseInt(t)

     return `${m.toLocaleString("en-US", {minimumIntegerDigits: 2})}:${s.toLocaleString("en-US", {minimumIntegerDigits: 2})}`;
    },
    // used from item_tab_nav.vue
    trackViewed: function(){
      track('transcription_viewed')
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/packs/colors.scss';

  .transcription {
    border-radius: 4px;

    &.processing {
      background: white;
    }
  }

  .full-width {
    text-align: center;
    padding: 2rem;
    color: $dark-gray;
  }

  .cta {
    display: inline-flex;
    align-items: center;
    padding: 0.563rem 2.438rem;
    border-radius: 4px;
    background-color: $bright-blue;
    color: white;
    font-size: .75rem;
    text-decoration: none;
  }

  .request-transcription {
    h2 {
      font-size: 1.75rem;
      font-weight: bold;
    }

    p {
      font-size: 1rem;
      margin: 0.875rem 0 1.75rem;
    }

  }

  .processing-transcription {
    h2 {
      margin-top: 1rem;
    }
  }

  .transcription-sentences {
    display: flex;
    flex-direction: column;
    position: relative;

    .sentence {
      display: flex;

      &:first-child .sentence-content {
        border-top-right-radius: 5px;
        border-top-left-radius: 5px;
      }

      &:last-child .sentence-content {
        border-bottom-right-radius: 5px;
        border-bottom-left-radius: 5px;
      }
    }

    .sentence-content {
      background: white;
      padding: 1rem 1rem 0;
      flex-grow: 1;
    }

    .sentence-time {
      padding: 1rem 0;
      // matches avatar width in comments section so they look the same
      min-width: 68px;
      text-align: center;
    }

    .time-badge {
      padding: 0.063rem 0.281rem 0.125rem 0.281rem;
      border-radius: 10px;
      background-color: $silver-chalice;
      color: white;
      font-size: .7rem;
      font-weight: bolder;
      cursor: pointer;

      .arrow-right {
        display: none;
      }

      &:hover {
        opacity: 1;
        background-color: $bright-blue;

        .arrow-right {
          display: inline;
        }
      }
    }

    .copy-transcription {
      position: absolute;
      top: -45px;
      right: 0;
      color: $dark-gray;
      cursor: pointer;
      font-size: .75rem;
    }
  }
</style>

<style lang="scss">
  @import '@/packs/application.scss';

  .transcription .copy-transcription {
    svg g {
      fill: $dark-gray;
    }
  }
</style>
